import config from '@/../config/britecore'

const aiChatModule = {
  namespaced: true,
  state: {
  },
  getters: {
    agentConfig: (state, getters, rootState, rootGetters) => {
      let config = {
        name: rootGetters['common/getContent']('agent_chat.agent_name'),
        iconPath: 'static/svg/fonolito-icon.svg',
        welcomeText:
          'I can help you file a claim or check the status of an existing claim.',
        quickActions: [
          { text: 'Help me file a claim', icon: 'clipboard' },
          { text: 'Check my claim status', icon: 'tasks' },
        ],
        acceptedFileTypes: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json,.jpg,.jpeg,.png,.gif,.webp',
        maxFileSize: 10 * 1024 * 1024, // 10MB max file size
      }
      return config
    }
  },
  actions: {
    /**
     * Build context string from claim data
     * @param {Object} context - Vuex context
     * @param {Object} payload - Context options
     * @param {string} payload.claimId - Claim ID
     * @param {string} payload.claimNumber - Claim Number
     * @param {string} payload.policyNumber - Policy Number
     * @returns {string|null} The context string or null
     */
    buildContext (context, { claimId, claimNumber, policyNumber }) {
      const parts = []
      if (claimId) parts.push(`Claim ID: ${claimId}`)
      if (claimNumber) parts.push(`Claim Number: ${claimNumber}`)
      if (policyNumber) parts.push(`Policy Number: ${policyNumber}`)

      return parts.length > 0 ? parts.join('\n') : null
    },

    /**
     * Create an SSE connection for streaming chat
     * @param {Object} context - Vuex context
     * @param {Object} payload - Connection options and callbacks
     * @param {string} payload.prompt - The user's message
     * @param {string} payload.sessionId - Optional session ID
     * @param {string} payload.context - Optional context string
     * @param {Function} payload.onStart - Called when stream starts
     * @param {Function} payload.onContent - Called with content chunks
     * @param {Function} payload.onToolExecution - Called during tool execution
     * @param {Function} payload.onComplete - Called when stream completes
     * @param {Function} payload.onError - Called on error
     * @param {Function} payload.onSessionId - Called with session ID
     * @returns {EventSource} The EventSource instance
     */
    createStreamingConnection (
      context,
      { prompt, sessionId, context: contextString, onStart, onContent, onToolExecution, onComplete, onError,
        onSessionId }
    ) {
      const params = new URLSearchParams()
      params.append('prompt', prompt)
      params.append('token', localStorage.token)

      if (sessionId) {
        params.append('session_id', sessionId)
      }
      if (contextString) {
        params.append('context', contextString)
      }

      const url = `${config.url}/ai-insured-chat-stream/?${params.toString()}`
      const eventSource = new EventSource(url)

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
          case 'start':
            if (onStart) onStart(data)
            break

          case 'done':
          case 'complete':
            if (data['briteapps-token']) {
              // we adding briteapps token here to keep it token up to date for the next requests
              context.commit('login/updateToken', data['briteapps-token'], { root: true })
            }
            if (onComplete) onComplete(data)
            break

          case 'tool_execution':
            if (data.session_id && onSessionId) {
              onSessionId(data.session_id)
            }
            if (onToolExecution) onToolExecution(data)
            break

          case 'content':
          case 'token':
            if (data.session_id && onSessionId) {
              onSessionId(data.session_id)
            }
            if (onContent) onContent(data.content || '')
            break

          case 'error':
            if (onError) onError(data.error)
            break

          default:
            console.warn('Unknown SSE event type:', data.type)
          }
        } catch (e) {
          console.error('Error parsing SSE data:', e)
          if (onError) onError('Failed to parse server response')
        }
      }

      eventSource.onerror = () => {
        if (onError) onError('Connection failed or was interrupted')
        // we checking token here to understand if error is related to token expiration
        context.dispatch('common/checkToken', {}, { root: true })
      }

      return eventSource
    },

    /**
     * Close an SSE connection
     * @param {Object} context - Vuex context
     * @param {EventSource} eventSource - The EventSource to close
     */
    closeConnection (context, eventSource) {
      if (eventSource) {
        eventSource.close()
      }
    },

    /**
     * Send a message with file attachments using POST with FormData
     * @param {Object} context - Vuex context
     * @param {Object} payload - Request payload
     * @param {string} payload.prompt - The user's message
     * @param {string} payload.sessionId - Optional session ID
     * @param {string} payload.context - Optional context string
     * @param {File[]} payload.files - Array of files to send
     * @returns {Promise<Object>} The response data
     */
    async sendWithFiles (context, { prompt, sessionId, context: contextString, files }) {
      const formData = new FormData()
      formData.append('prompt', prompt)
      formData.append('token', localStorage.token)

      if (sessionId) {
        formData.append('session_id', sessionId)
      }

      if (contextString) {
        formData.append('context', contextString)
      }

      // Append each file
      files.forEach((file) => {
        formData.append('files', file)
      })

      const url = `${config.url}/ai-insured-chat/`
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      })

      const headers = {}
      response.headers.forEach((value, key) => {
        headers[key] = value
      })

      if (headers['briteapps-token']) {
        // update briteapps token to keep it up to date for the next requests
        context.commit('login/updateToken', headers['briteapps-token'], { root: true })
      }

      if (response.status === 401) {
        // check token to understand if error is related to token expiration
        context.dispatch('common/checkToken', {}, { root: true })
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return response.json()
    },
  }
}

export default aiChatModule
